import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Send, X, CornerDownRight } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import { FollowUpRecipient } from '../../services/guestAPI';

interface FollowUpConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the GuestEvent ids the host unticked, to be skipped by the send. */
  onConfirm: (excludedGuestEventIds: string[]) => void;
  recipients: FollowUpRecipient[];
  /** GuestEvent ids currently unticked. Owned by the parent so it survives re-renders. */
  excludedIds: Set<string>;
  onToggleRecipient: (guestEventId: string) => void;
  isLoadingRecipients?: boolean;
  loadError?: string | null;
  isLoading?: boolean;
}

/**
 * Confirmation step for the Follow Up send.
 *
 * Lists every recipient the backend resolved (primary guests, with their companions
 * grouped underneath), each with a Send flag that defaults to true. Unticking a row
 * excludes just that person - so you can chase one companion without touching the rest.
 */
const FollowUpConfirmationModal: React.FC<FollowUpConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  recipients,
  excludedIds,
  onToggleRecipient,
  isLoadingRecipients = false,
  loadError = null,
  isLoading = false,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const isDark = theme === 'dark';

  // Group companions under their primary guest, preserving backend order.
  const groups = useMemo(() => {
    const byPrimary = new Map<string, { primaryName: string; rows: FollowUpRecipient[] }>();

    recipients.forEach((r) => {
      const key = r.primary_guest_event_id;
      if (!byPrimary.has(key)) {
        byPrimary.set(key, { primaryName: r.primary_guest_name, rows: [] });
      }
      byPrimary.get(key)!.rows.push(r);
    });

    // Guest first, then their companions, then the question asking the guest to confirm one
    const order: Record<FollowUpRecipient['type'], number> = { guest: 0, companion: 1, question: 2 };
    byPrimary.forEach((group) => {
      group.rows.sort((a, b) => order[a.type] - order[b.type]);
    });

    // A group can contain only companions - when the primary guest already responded and so
    // isn't a recipient. Flag those so the table can still show whose companions these are.
    return Array.from(byPrimary.entries()).map(([primaryId, group]) => ({
      primaryId,
      primaryName: group.primaryName,
      rows: group.rows,
      hasGuestRow: group.rows.some((r) => r.type === 'guest'),
    }));
  }, [recipients]);

  const selectedCount = recipients.filter((r) => !excludedIds.has(r.guest_event_id)).length;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!isLoading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const cellBase = `px-3 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />

      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div
          className={`relative w-full transform overflow-hidden rounded-lg shadow-xl transition-all ${
            isDark ? 'bg-gray-900/95 border border-gray-700/50' : 'bg-white border border-gray-200/50'
          }`}
        >
          {/* Header */}
          <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${
                  isDark ? 'bg-teal-900/30' : 'bg-teal-100'
                }`}>
                  <Send className={`h-5 w-5 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Send Follow-Ups
                  </h3>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Only people who haven&apos;t responded are listed. Untick anyone you want to skip.
                  </p>
                </div>
              </div>

              {!isLoading && (
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDark
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-4">
            {isLoadingRecipients && (
              <div className={`p-4 rounded-lg border ${
                isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Working out who still needs a follow-up...
                </p>
              </div>
            )}

            {loadError && !isLoadingRecipients && (
              <div className={`p-4 rounded-lg border ${
                isDark ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-800'}`}>{loadError}</p>
              </div>
            )}

            {!isLoadingRecipients && !loadError && recipients.length === 0 && (
              <div className={`p-4 rounded-lg border ${
                isDark ? 'bg-green-900/20 border-green-700/50' : 'bg-green-50 border-green-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                  Everyone in your selection has already responded. There is nobody to follow up with.
                </p>
              </div>
            )}

            {!isLoadingRecipients && !loadError && recipients.length > 0 && (
              <div className={`rounded-lg border overflow-hidden ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                        <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Recipient
                        </th>
                        <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Type
                        </th>
                        <th className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Send
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group, groupIndex) => (
                        <React.Fragment key={group.primaryId}>
                          {/* Context row: the primary guest already responded, so they are
                              not a recipient, but their name still anchors the companions. */}
                          {!group.hasGuestRow && (
                            <tr className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
                              groupIndex % 2 === 1 ? (isDark ? 'bg-gray-800/30' : 'bg-gray-50/50') : ''
                            }`}>
                              <td className={cellBase}>
                                <span className={`font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {group.primaryName}
                                </span>
                              </td>
                              <td className={cellBase}>
                                <span className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Guest &middot; already responded
                                </span>
                              </td>
                              <td className={`px-3 py-2 text-center text-xs ${
                                isDark ? 'text-gray-600' : 'text-gray-400'
                              }`}>
                                &mdash;
                              </td>
                            </tr>
                          )}
                          {group.rows.map((row) => {
                          const willSend = !excludedIds.has(row.guest_event_id);
                          const isCompanion = row.type === 'companion';
                          const isQuestion = row.type === 'question';
                          const isIndented = isCompanion || isQuestion;
                          const typeLabel = isQuestion
                            ? 'Question to guest'
                            : isCompanion
                              ? 'Companion'
                              : 'Guest';
                          const typeClasses = isQuestion
                            ? isDark
                              ? 'bg-amber-900/30 text-amber-300'
                              : 'bg-amber-100 text-amber-700'
                            : isCompanion
                              ? isDark
                                ? 'bg-purple-900/30 text-purple-300'
                                : 'bg-purple-100 text-purple-700'
                              : isDark
                                ? 'bg-blue-900/30 text-blue-300'
                                : 'bg-blue-100 text-blue-700';
                          return (
                            <tr
                              key={row.guest_event_id}
                              className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
                                groupIndex % 2 === 1 ? (isDark ? 'bg-gray-800/30' : 'bg-gray-50/50') : ''
                              } ${willSend ? '' : 'opacity-50'}`}
                            >
                              <td className={cellBase}>
                                <div className="flex items-center gap-1.5">
                                  {isIndented && (
                                    <CornerDownRight className={`h-3.5 w-3.5 flex-shrink-0 ml-3 ${
                                      isDark ? 'text-gray-500' : 'text-gray-400'
                                    }`} />
                                  )}
                                  <span className={isIndented ? '' : 'font-medium'}>{row.name}</span>
                                </div>
                              </td>
                              <td className={cellBase}>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeClasses}`}
                                  title={
                                    isQuestion
                                      ? 'Asks the main guest to confirm this companion. The only way to reach a companion with no phone of their own.'
                                      : undefined
                                  }
                                >
                                  {typeLabel}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={willSend}
                                    disabled={isLoading}
                                    onChange={() => onToggleRecipient(row.guest_event_id)}
                                    className="h-4 w-4 rounded cursor-pointer"
                                    aria-label={`Send follow-up to ${row.name}`}
                                  />
                                  <span className={`text-xs font-medium ${
                                    willSend
                                      ? isDark ? 'text-green-400' : 'text-green-700'
                                      : isDark ? 'text-gray-500' : 'text-gray-400'
                                  }`}>
                                    {willSend ? 'True' : 'False'}
                                  </span>
                                </label>
                              </td>
                            </tr>
                          );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isLoadingRecipients && !loadError && recipients.length > 0 && (
              <div className={`p-4 rounded-lg border-2 ${
                isDark ? 'bg-yellow-900/20 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  {selectedCount === 0 ? (
                    <>Every row is unticked, so <strong>nothing will be sent</strong>.</>
                  ) : (
                    <>
                      A follow-up will be sent to{' '}
                      <strong>{selectedCount} of {recipients.length}</strong> listed recipient
                      {recipients.length > 1 ? 's' : ''}.
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={`px-6 py-5 border-t flex items-center justify-end gap-3 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => onConfirm(Array.from(excludedIds))}
              disabled={isLoading || isLoadingRecipients || selectedCount === 0}
              loading={isLoading}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FollowUpConfirmationModal;
